import { Controller } from "@hotwired/stimulus"

// Address step of the place connection flow (docs/connexio-de-llocs.md):
// Mapbox suggestions with debounce, a static confirmation map, and a manual
// fallback when Mapbox is not configured or the address is not found.
// The static map is an <img> against the Static Images API — Mapbox GL is
// deliberately not loaded (docs/carrega-llibreries-esporadiques.md).
const STATIC_STYLE_PATH = "mapbox/streets-v12"
const STATIC_MAP_WIDTH = 800
const STATIC_MAP_HEIGHT = 500
const STATIC_MAP_ZOOM = 15

export default class extends Controller {
  static targets = [
    "searchContainer",
    "searchInput",
    "results",
    "manualContainer",
    "manualInput",
    "manualLink",
    "preview",
    "formattedAddress",
    "formattedMeta",
    "mapContainer",
    "status",
    "raw",
    "street",
    "streetNumber",
    "postalCode",
    "city",
    "region",
    "country",
    "latitude",
    "longitude",
    "mapboxPlaceId",
    "source"
  ]

  static values = {
    url: String,
    configured: { type: Boolean, default: true },
    searchingText: String,
    noResultsText: String,
    errorText: String,
    accessToken: String,
    mapAlt: String
  }

  connect() {
    this.resultsData = []
    this.abortController = null
    this.debounceTimer = null

    if (!this.configuredValue) {
      this.manualInputTarget.value = this.rawTarget.value
      this.showManual()
      return
    }

    if (this.rawTarget.value && this.sourceTarget.value === "mapbox") {
      this.showPreview({ raw: this.rawTarget.value, secondary_text: this.formatSecondaryText() })
    } else if (this.rawTarget.value) {
      this.manualInputTarget.value = this.rawTarget.value
      this.showManual()
    } else {
      this.showSearch()
    }
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.abortController) this.abortController.abort()
  }

  searchChanged() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.search(), 220)
  }

  async search() {
    const query = this.searchInputTarget.value.trim()

    if (query.length < 3) {
      this.clearResults()
      this.hideStatus()
      return
    }

    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    this.showStatus(this.searchingTextValue)

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error(this.errorTextValue)

      this.renderResults(await response.json())
    } catch (error) {
      if (error.name === "AbortError") return

      this.showStatus(this.errorTextValue)
      this.clearResults()
    }
  }

  selectResult(event) {
    const result = this.resultsData[Number(event.currentTarget.dataset.index)]
    if (!result) return

    this.rawTarget.value = result.raw || ""
    this.streetTarget.value = result.street || ""
    this.streetNumberTarget.value = result.street_number || ""
    this.postalCodeTarget.value = result.postal_code || ""
    this.cityTarget.value = result.city || ""
    this.regionTarget.value = result.region || ""
    this.countryTarget.value = result.country || ""
    this.latitudeTarget.value = result.latitude || ""
    this.longitudeTarget.value = result.longitude || ""
    this.mapboxPlaceIdTarget.value = result.mapbox_place_id || ""
    this.sourceTarget.value = "mapbox"
    this.manualInputTarget.value = ""

    this.showPreview(result)
    this.clearResults()
    this.hideStatus()
  }

  switchToManual() {
    this.clearResolvedFields()
    this.showManual()
    this.manualInputTarget.focus()
  }

  manualInputChanged() {
    this.clearResolvedFields({ preserveRaw: true })
    this.rawTarget.value = this.manualInputTarget.value
    this.sourceTarget.value = "user_input"
  }

  changeAddress() {
    this.clearResolvedFields()
    this.manualInputTarget.value = ""
    this.searchInputTarget.value = ""
    this.showSearch()
    this.searchInputTarget.focus()
  }

  renderResults(results) {
    this.resultsData = Array.isArray(results) ? results : []

    if (this.resultsData.length === 0) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
      this.showStatus(this.noResultsTextValue)
      return
    }

    this.hideStatus()
    this.resultsTarget.classList.remove("hidden")
    this.resultsTarget.innerHTML = this.resultsData.map((result, index) => `
      <button
        type="button"
        data-action="address-autocomplete#selectResult"
        data-index="${index}"
        class="block w-full border-b border-border px-4 py-3 text-left last:border-b-0"
      >
        <div class="text-sm font-medium text-foreground">${this.escapeHtml(result.raw || "")}</div>
        <div class="mt-1 text-sm text-muted-foreground">${this.escapeHtml(result.secondary_text || "")}</div>
      </button>
    `).join("")
  }

  showPreview(result) {
    this.searchContainerTarget.classList.add("hidden")
    this.manualContainerTarget.classList.add("hidden")
    this.manualLinkTarget.classList.add("hidden")
    this.previewTarget.classList.remove("hidden")
    this.formattedAddressTarget.textContent = result.raw || ""
    this.formattedMetaTarget.textContent = result.secondary_text || this.formatSecondaryText()
    this.renderStaticMap()
  }

  showManual() {
    this.previewTarget.classList.add("hidden")
    this.searchContainerTarget.classList.add("hidden")
    this.resultsTarget.classList.add("hidden")
    this.manualLinkTarget.classList.add("hidden")
    this.manualContainerTarget.classList.remove("hidden")
    this.hideStatus()
  }

  showSearch() {
    this.previewTarget.classList.add("hidden")
    this.manualContainerTarget.classList.add("hidden")
    this.searchContainerTarget.classList.remove("hidden")
    if (this.configuredValue) this.manualLinkTarget.classList.remove("hidden")
  }

  clearResults() {
    this.resultsData = []
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  clearResolvedFields({ preserveRaw = false } = {}) {
    if (!preserveRaw) this.rawTarget.value = ""

    this.streetTarget.value = ""
    this.streetNumberTarget.value = ""
    this.postalCodeTarget.value = ""
    this.cityTarget.value = ""
    this.regionTarget.value = ""
    this.countryTarget.value = ""
    this.latitudeTarget.value = ""
    this.longitudeTarget.value = ""
    this.mapboxPlaceIdTarget.value = ""
    this.sourceTarget.value = preserveRaw ? "user_input" : ""
  }

  formatSecondaryText() {
    return [
      this.postalCodeTarget.value,
      this.cityTarget.value,
      this.regionTarget.value,
      this.countryTarget.value
    ].filter(Boolean).join(" · ")
  }

  showStatus(message) {
    if (!message) {
      this.hideStatus()
      return
    }

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
  }

  hideStatus() {
    this.statusTarget.textContent = ""
    this.statusTarget.classList.add("hidden")
  }

  renderStaticMap() {
    const lat = parseFloat(this.latitudeTarget.value)
    const lng = parseFloat(this.longitudeTarget.value)
    this.mapContainerTarget.innerHTML = ""

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !this.accessTokenValue) {
      this.mapContainerTarget.classList.add("hidden")
      return
    }

    const img = document.createElement("img")
    img.src = this.staticMapImageUrl(lng, lat)
    img.alt = this.mapAltValue || ""
    img.className = "h-full w-full object-cover"
    img.loading = "lazy"
    img.decoding = "async"
    img.width = STATIC_MAP_WIDTH
    img.height = STATIC_MAP_HEIGHT
    this.mapContainerTarget.classList.remove("hidden")
    this.mapContainerTarget.appendChild(img)
  }

  staticMapImageUrl(lng, lat) {
    const token = encodeURIComponent(this.accessTokenValue)
    const lngS = lng.toFixed(5)
    const latS = lat.toFixed(5)
    const overlay = `pin-s(${lngS},${latS})`
    const coord = `${lngS},${latS},${STATIC_MAP_ZOOM}`
    const path = `${overlay}/${coord}/${STATIC_MAP_WIDTH}x${STATIC_MAP_HEIGHT}@2x`
    return `https://api.mapbox.com/styles/v1/${STATIC_STYLE_PATH}/static/${path}?access_token=${token}`
  }

  escapeHtml(text) {
    const element = document.createElement("div")
    element.textContent = text
    return element.innerHTML
  }
}
